/**
 * How the Report templates tab asks for its thumbnails (D27): the renderer
 * holds at most 4 waiters and answers 503 when full, so a tab of many
 * templates must not ask for every page at once.
 *
 * - At most `lanes` pages are drawn at a time; the rest wait their turn here.
 * - A busy answer is asked again after a wait (its Retry-After when it gave
 *   one), up to `tries` in all; any other refusal is final.
 * - A page drawn a moment ago is kept for `keepMs`, so going to a template and
 *   back draws nothing again. Two rows asking for the same page share one draw.
 *   A failure is never kept.
 * - A page is kept for the viewer who saw it: the preview prints under the
 *   viewer's mask, so `seenBy` a different viewer (another person, another
 *   business, viewing as someone) and `forget` (a sign-out) drop every page kept.
 */

export type ThumbQueueOptions = {
  readonly lanes: number;
  readonly tries: number;
  readonly isBusy: (thrown: unknown) => boolean;
  readonly wait: (ms: number) => Promise<void>;
  readonly now: () => number;
  readonly keepMs: number;
  /** The wait before asking again; by default the answer's Retry-After, else 1 s, 2 s, 4 s. */
  readonly delayOf?: (thrown: unknown, attempt: number) => number;
};

export type ThumbQueue<T> = {
  readonly run: (key: string, job: () => Promise<T>) => Promise<T>;
  /** Who the pages are drawn for now: a viewer other than the last drops every page kept. */
  readonly seenBy: (viewer: string) => void;
  /** Drops every page kept. */
  readonly forget: () => void;
};

const retryAfterMs = (thrown: unknown): number | null => {
  const body = typeof thrown === 'object' && thrown !== null && 'body' in thrown ? (thrown as { body: unknown }).body : null;
  const seconds = typeof body === 'object' && body !== null && 'retryAfter' in body ? (body as { retryAfter: unknown }).retryAfter : null;
  return typeof seconds === 'number' && seconds > 0 ? seconds * 1000 : null;
};

const backoff = (thrown: unknown, attempt: number): number => retryAfterMs(thrown) ?? 1000 * 2 ** (attempt - 1);

export function makeThumbQueue<T>(options: ThumbQueueOptions): ThumbQueue<T> {
  const { lanes, tries, isBusy, wait, now, keepMs } = options;
  const delayOf = options.delayOf ?? backoff;
  const kept = new Map<string, { readonly at: number; readonly answer: Promise<T> }>();
  const waiting: Array<() => void> = [];
  let drawing = 0;
  let viewer: string | null = null;

  const lane = async (): Promise<void> => {
    if (drawing < lanes) {
      drawing += 1;
      return;
    }
    // The lane is handed over by the one that finishes, so `drawing` stays counted.
    await new Promise<void>((go) => waiting.push(go));
  };
  const release = (): void => {
    const next = waiting.shift();
    if (next === undefined) drawing -= 1;
    else next();
  };

  const draw = async (job: () => Promise<T>): Promise<T> => {
    for (let attempt = 1; ; attempt += 1) {
      await lane();
      let outcome: { readonly ok: true; readonly value: T } | { readonly ok: false; readonly thrown: unknown };
      try {
        outcome = { ok: true, value: await job() };
      } catch (thrown) {
        outcome = { ok: false, thrown };
      } finally {
        // The wait before asking again is spent outside the lane, so the others go on.
        release();
      }
      if (outcome.ok) return outcome.value;
      if (!isBusy(outcome.thrown) || attempt >= tries) throw outcome.thrown;
      await wait(delayOf(outcome.thrown, attempt));
    }
  };

  return {
    run: (key, job) => {
      const hit = kept.get(key);
      if (hit !== undefined && now() - hit.at <= keepMs) return hit.answer;
      const answer = draw(job);
      kept.set(key, { at: now(), answer });
      answer.catch(() => {
        if (kept.get(key)?.answer === answer) kept.delete(key);
      });
      return answer;
    },
    seenBy: (next) => {
      if (next === viewer) return;
      viewer = next;
      kept.clear();
    },
    forget: () => {
      viewer = null;
      kept.clear();
    },
  };
}
